import { useEffect, useState, type FormEvent } from 'react';
import { Button, Field, Modal, Select, Textarea } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { ApiError, api } from '@/lib/api';
import { denialReason } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';
import type { Deal, DealLostReason } from '@/lib/types';

/**
 * The lost-reason pick list (migration 285, owner decision 26, 2026-10-16),
 * asked whenever a deal moves to a stage carrying `isLost`.
 *
 * Two exports:
 *
 * - `LostReasonFields` — the Reason select and the Note box, for a form that
 *   already owns the PATCH (`DealFormModal` sends them with its own `stageId`).
 * - `DealLostReasonDialog` — the same two fields in their own `Modal`, which
 *   makes ONE `PATCH /deals/:id` carrying `stageId` + `lostReasonId` +
 *   `lostNote`. Cancelling writes nothing, so the stage is unchanged.
 *
 * ⚠️ The decision to ask is made by the CALLER from the stage's `isLost` flag,
 * never from its name.
 *
 * Only ACTIVE reasons are offered: a retired reason is a 422 on write. Rows
 * render in the order the server sends them (`sortOrder`, then name).
 */

const LOST_NOTE_MAX = 500;

export interface LostReasonFieldsProps {
  /** The chosen reason id as a string, `''` for none. */
  reasonId: string;
  note: string;
  onReasonChange: (next: string) => void;
  onNoteChange: (next: string) => void;
  reasonError?: string | undefined;
  noteError?: string | undefined;
  disabled?: boolean;
}

type ReasonsState =
  | { kind: 'loading' }
  | { kind: 'ready'; rows: DealLostReason[] }
  | { kind: 'error'; message: string };

export function LostReasonFields({
  reasonId,
  note,
  onReasonChange,
  onNoteChange,
  reasonError,
  noteError,
  disabled = false,
}: LostReasonFieldsProps) {
  const [reasons, setReasons] = useState<ReasonsState>({ kind: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    api
      .listDealLostReasons(controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setReasons({ kind: 'ready', rows: response.data.filter((row) => row.isActive) });
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setReasons({
          kind: 'error',
          message:
            caught instanceof ApiError && caught.status === 404
              ? 'This server has no lost-reason list yet.'
              : caught instanceof Error
                ? caught.message
                : 'The lost reasons could not be loaded.',
        });
      });
    return () => controller.abort();
  }, []);

  const failed = reasons.kind === 'error';

  return (
    <div className="grid min-w-0 gap-3">
      {failed && (
        <Notice tone="danger" role="alert" title="Couldn’t load the lost reasons.">
          <span className="[overflow-wrap:anywhere]">{reasons.message}</span>
        </Notice>
      )}
      <Field label="Lost reason" required error={reasonError}>
        {({ id, 'aria-describedby': describedBy }) => (
          <Select
            id={id}
            value={reasonId}
            onChange={(event) => onReasonChange(event.target.value)}
            disabled={disabled || reasons.kind !== 'ready'}
            invalid={Boolean(reasonError)}
            aria-describedby={describedBy}
            required
          >
            <option value="">{reasons.kind === 'loading' ? 'Loading…' : 'None chosen'}</option>
            {reasons.kind === 'ready' &&
              reasons.rows.map((row) => (
                <option key={row.id} value={String(row.id)}>
                  {row.name}
                </option>
              ))}
          </Select>
        )}
      </Field>
      <Field label="Note" error={noteError}>
        {({ id, 'aria-describedby': describedBy }) => (
          <Textarea
            id={id}
            value={note}
            onChange={(event) => onNoteChange(event.target.value)}
            maxLength={LOST_NOTE_MAX}
            rows={3}
            disabled={disabled}
            invalid={Boolean(noteError)}
            aria-describedby={describedBy}
          />
        )}
      </Field>
    </div>
  );
}

export interface DealLostReasonDialogProps {
  open: boolean;
  deal: { id: number; dealName: string };
  /** The `isLost` stage being moved to. */
  stage: { id: number; name: string };
  /** Cancelled: nothing was written. */
  onCancel: () => void;
  /** The PATCH landed; the record the server returned. */
  onSaved: (saved: Deal) => void;
}

export function DealLostReasonDialog({
  open,
  deal,
  stage,
  onCancel,
  onSaved,
}: DealLostReasonDialogProps) {
  const { ability } = useAuth();
  const [reasonId, setReasonId] = useState('');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<{ lostReasonId?: string; lostNote?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /* A fresh form per opening: a reason picked for one move is not an answer for the next. */
  const [openedFor, setOpenedFor] = useState<string | null>(null);
  const key = open ? `${deal.id}:${stage.id}` : null;
  if (key !== openedFor) {
    setOpenedFor(key);
    setReasonId('');
    setNote('');
    setErrors({});
    setFormError(null);
    setSaving(false);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    const trimmed = note.trim();
    const found: { lostReasonId?: string; lostNote?: string } = {};
    if (reasonId === '') found.lostReasonId = 'Choose why the deal was lost.';
    if (trimmed.length > LOST_NOTE_MAX) found.lostNote = `Must be ${LOST_NOTE_MAX} characters or fewer.`;
    setErrors(found);
    setFormError(null);
    if (Object.keys(found).length > 0) return;

    setSaving(true);
    try {
      const response = await api.updateDeal(deal.id, {
        stageId: stage.id,
        lostReasonId: Number(reasonId),
        lostNote: trimmed === '' ? null : trimmed,
      });
      setSaving(false);
      onSaved(response.data);
    } catch (caught) {
      setSaving(false);
      if (caught instanceof ApiError) {
        if (caught.status === 403) {
          setFormError(
            `${
              denialReason(ability, 'update', 'deals') ??
              'Your account no longer has permission to change this deal.'
            } Nothing was saved.`,
          );
          return;
        }
        if (caught.status === 404) {
          setFormError('This deal no longer exists, so the change could not be saved.');
          return;
        }
        const fields = caught.fieldErrors;
        const routed: { lostReasonId?: string; lostNote?: string } = {};
        if (fields.lostReasonId) routed.lostReasonId = fields.lostReasonId;
        if (fields.lostNote) routed.lostNote = fields.lostNote;
        setErrors(routed);
        const rest = Object.entries(fields)
          .filter(([path]) => path !== 'lostReasonId' && path !== 'lostNote')
          .map(([, message]) => message);
        if (rest.length > 0 || Object.keys(routed).length === 0) {
          setFormError(
            [...rest, ...(rest.length === 0 ? [caught.formMessage ?? caught.message] : [])].join(
              ' ',
            ),
          );
        }
        return;
      }
      setFormError('Could not reach the server. Check your connection and try again.');
    }
  }

  const formId = `deal-lost-reason-${deal.id}`;

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!saving) onCancel();
      }}
      title={`Move ${deal.dealName || `Deal #${deal.id}`} to ${stage.name}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form={formId} loading={saving}>
            Mark as lost
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={(event) => void handleSubmit(event)} className="grid gap-3" noValidate>
        {formError && (
          <Notice tone="danger" role="alert" title="The stage was not changed.">
            <span className="[overflow-wrap:anywhere]">{formError}</span>
          </Notice>
        )}
        <LostReasonFields
          reasonId={reasonId}
          note={note}
          onReasonChange={(next) => {
            setReasonId(next);
            setErrors((prev) => ({ ...prev, lostReasonId: undefined }));
          }}
          onNoteChange={setNote}
          reasonError={errors.lostReasonId}
          noteError={errors.lostNote}
          disabled={saving}
        />
      </form>
    </Modal>
  );
}
